import {
  Avatar,
  Box,
  Button,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { createChat } from "../../pages/Chat/firestore";
import { useState } from "react";
import { UserInfo } from "../../types";
import { ClosePopupIcon } from "../../assets/icons/MUI/EntitiesIcons";
import { colors } from "../../theme/palette";
import { useProfileData } from "../../hooks/useProfileData";
import { useNavigate } from "react-router-dom";
import AutoCompleteSearch from "./AutoCompleteSearch";
import { useDataFormatter } from "../../hooks/useDataFormatter";
import { useTranslation } from "react-i18next";
import { getChatThreadRoute } from "../../routers/routeUtils";

const NewChatPopUp = ({
  open,
  handleClose,
  receiverInfo,
}: {
  open: boolean;
  handleClose: () => void;
  receiverInfo?: UserInfo;
}) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState<string | null>(null);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [searchedReceiverName, setSearchedReceiverName] = useState<
    string | null
  >(null);
  const [searchedReceiverId, setSearchedReceiverId] = useState<number | null>(
    null
  );
  const [messageError, setMessageError] = useState<boolean>(false);
  const navigate = useNavigate();
  const { data } = useProfileData();
  const { fullName: senderName, id: senderId } = data;
  const {
    fullName: receiverFullName,
    firstTwoChr: receiverFirstTwoChr,
    media: receiverMedia,
    role: receiverRole,
    id: receiverId,
  } = useDataFormatter(receiverInfo);

  const handleCloseModal = () => {
    handleClose();
    setMessage(null);
    setSearchedReceiverName(null);
    setSearchedReceiverId(null);
    setMessageError(false);
  };

  const handleSendMessage = async () => {
    setIsSending(true);
    const selectedReceiverId = receiverId || searchedReceiverId;
    const selectedReceiverName = receiverFullName || searchedReceiverName;

    if (!message || message.length === 0) {
      setMessageError(true);
      return;
    }
    setMessageError(false);
    if (
      !senderId ||
      !selectedReceiverId ||
      !senderName ||
      !selectedReceiverName
    )
      return;
    const threadId = await createChat(
      message,
      senderId,
      selectedReceiverId,
      senderName,
      selectedReceiverName
    );
    navigate(getChatThreadRoute(threadId, selectedReceiverId));
    setIsSending(false);
    handleClose();
  };
  return (
    <Dialog onClose={handleCloseModal} open={open}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "435px",
          borderRadius: "12px",
          background: colors.White,
        }}
      >
        <IconButton
          sx={{
            position: "absolute",
            top: "15px",
            left: "15px",
            cursor: "pointer",
          }}
          onClick={handleCloseModal}
        >
          <ClosePopupIcon />
        </IconButton>
        <Typography
          variant="body14Bold"
          sx={{
            display: "block",
            borderBottom: "1px solid #EAECF0",
            padding: "21px 0 18px",
            marginBottom: "16px",
            textAlign: "center",
          }}
        >
          {t("components.newChatPopUp.newMessage")}
        </Typography>
        {receiverInfo ? (
          <Box
            sx={{
              display: "flex",
              columnGap: "12px",
              alignItems: "flex-start",
              margin: "0 28px 32px",
            }}
          >
            <Avatar
              style={{ width: "48px", height: "48px" }}
              src={receiverMedia}
            >
              {receiverFirstTwoChr}
            </Avatar>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body14Regular">
                {receiverFullName}
              </Typography>
              <Typography
                variant="body12Regular"
                sx={{ color: colors.Dark600 }}
              >
                {receiverRole}
              </Typography>
            </Box>
          </Box>
        ) : (
          <AutoCompleteSearch
            setSearchedReceiverName={setSearchedReceiverName}
            setSearchedReceiverId={setSearchedReceiverId}
          />
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            margin: "0 28px 16px",
            alignItems: "flex-end",
          }}
        >
          <TextField
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "8px !important",
                paddingInlineEnd: "0 !important",
              },
            }}
            value={message || ""}
            multiline
            rows={4}
            placeholder={t("components.newChatPopUp.writeAMessage")}
            error={messageError}
            helperText={
              messageError ? t("components.newChatPopUp.writeYourMessage") : ""
            }
            onChange={(event) => {
              setMessage(event.target.value);
              if (event.target.value.length === 0) {
                setMessageError(true);
              } else {
                setMessageError(false);
              }
            }}
          />
          <Button
            variant="primary-sm"
            sx={{ width: "fit-content" }}
            onClick={handleSendMessage}
            disabled={isSending}
          >
            {t("components.newChatPopUp.send")}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default NewChatPopUp;
